import { AttachmentFile } from '@nextforce/api'
import { useHeaderHeight } from '@react-navigation/elements'
import { Input as BaseInput, Divider } from '@rneui/base'
import { Icon, Switch, Text, makeStyles, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { MessageAccessoryBar } from 'chat/messages/views/accessory_bar'
import {
  convertNativeAttachmentToFile,
  handleTextChanged,
} from 'chat/shared/util'
import React, { useCallback, useRef, useState } from 'react'
import {
  ActivityIndicator,
  ImageChangeEventData,
  Platform,
  Pressable,
  ScrollView,
  TextInput,
  View,
} from 'react-native'
import { FlatList } from 'react-native-gesture-handler'
import { KeyboardAvoidingView } from 'react-native-keyboard-controller'
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context'
import { useThemeChange } from 'settings/change_theme'
import { AttachmentPreviewRow } from 'shared/components/chat/attachment_preview'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { TextInputBox } from 'shared/components/text_input_box'
import { getPrimaryRole } from 'shared/utils'
import { ComposeGroup, ComposeUser, Recipient } from './queries/shared/types'

export const COMPOSE_SCREEN_ID = 'compose'

interface Props {
  recipients: (ComposeGroup | ComposeUser)[] | undefined
  selectedRecipients: (ComposeGroup | ComposeUser)[]
  isLoading: boolean
  isReadOnly: boolean
  messageBody: string
  onRecipientPress(recipient: Recipient): void
  onSelectedRecipientPress(recipient: Recipient): void
  setSearchTerm(value: string): void
  setMessageBody(text: string): void
  pasteMessageAttachments(files: AttachmentFile[]): void
  onHandleBackspace(): void
  enableUrgentMessage?: boolean
  isUrgentMessage: boolean
  setIsUrgentMessage: (isUrgent: boolean) => void
}

export function ComposeView({
  recipients,
  selectedRecipients,
  isLoading,
  isReadOnly,
  messageBody,
  onRecipientPress,
  onSelectedRecipientPress,
  pasteMessageAttachments,
  setSearchTerm,
  setMessageBody,
  onHandleBackspace,
  enableUrgentMessage,
  isUrgentMessage,
  setIsUrgentMessage,
}: Props) {
  const { dark } = useThemeChange()
  const styles = useStyles(dark)
  const { theme } = useTheme()
  const textInputRef = useRef<BaseInput & TextInput>(null)
  const selectedRecipientsRef = useRef<ScrollView>(null)
  const [inputText, setInputText] = useState('')
  const characterLimitAlertShown = useRef(false)
  const searchInput = () => (
    <Pressable
      style={styles.searchInputComponent}
      onPress={() => textInputRef.current?.focus()}
    >
      <TextInput
        autoFocus
        spellCheck={false}
        allowFontScaling={false}
        selectionColor={theme.colors?.primary}
        ref={textInputRef}
        editable={!isReadOnly}
        style={styles.searchTextInput}
        onChangeText={value => {
          setInputText(value)
          setSearchTerm(value)
        }}
        onKeyPress={({ nativeEvent }) => {
          if (nativeEvent.key === 'Backspace' && inputText === '') {
            onHandleBackspace()
          }
        }}
      />
      <ActivityIndicator
        style={{
          paddingHorizontal: 5,
        }}
        animating={isReadOnly ? false : isLoading}
        size={'small'}
        color={theme.colors?.primary}
      />
    </Pressable>
  )

  const handleAttachmentPaste = useCallback(
    async (data: ImageChangeEventData) => {
      const attachmentFile = await convertNativeAttachmentToFile({
        data,
      })
      if (attachmentFile) {
        pasteMessageAttachments([attachmentFile])
      }
    },
    [pasteMessageAttachments]
  )

  const selectedRecipientsView = () => {
    const Pill = ({ item }: { item: ComposeGroup | ComposeUser }) => (
      <View style={styles.selectedListItem}>
        <Text style={styles.selectedListItemText}>
          {item.type === 'group'
            ? item.title
            : item.firstName + ' ' + item.lastName}
        </Text>
        <Icon
          name="times"
          type="font-awesome-5"
          solid
          onPress={() => onSelectedRecipientPress(item)}
          color={theme.colors?.grey0}
          size={15}
          containerStyle={{
            paddingHorizontal: 5,
          }}
        />
      </View>
    )
    const selectedRecipientsMap = selectedRecipients.map(item => {
      return <Pill key={item.id} item={item} />
    })
    return selectedRecipientsMap
  }

  const headerHeight = useHeaderHeight()
  const insets = useSafeAreaInsets()

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={{ flex: 1 }}>
      <KeyboardAvoidingView
        style={styles.container}
        behavior={'padding'}
        keyboardVerticalOffset={headerHeight}
      >
        <Text style={styles.descriptionText}>
          {t('composeMessageDescriptionText')}
        </Text>
        <View style={styles.selectedRecipientRow}>
          <Text style={styles.listText}>{t('to') + ':'}</Text>
          <ScrollView
            ref={selectedRecipientsRef}
            style={styles.selectedList}
            contentContainerStyle={styles.selectedListContainer}
            onContentSizeChange={() =>
              selectedRecipientsRef.current?.scrollToEnd()
            }
          >
            {selectedRecipientsView()}
            {!isReadOnly && searchInput()}
          </ScrollView>
        </View>
        <View
          style={{
            flex: 1,
          }}
        >
          {inputText.length >= 1 && recipients ? (
            recipients.length > 0 ? (
              <FlatList
                keyboardShouldPersistTaps="handled"
                style={styles.recipientList}
                contentContainerStyle={styles.recipientListContainer}
                data={recipients}
                renderItem={({ item }) => (
                  <RecipientListItem
                    item={item}
                    onPress={() => {
                      textInputRef.current?.clear()
                      textInputRef.current?.focus()
                      setInputText('')
                      setSearchTerm('')
                      onRecipientPress(item)
                    }}
                  />
                )}
              />
            ) : (
              <View style={[styles.recipientList]}>
                <View style={[styles.recipientListContainer, styles.listItem]}>
                  <Text style={{ textAlign: 'center' }}>{t('noResults')}</Text>
                </View>
              </View>
            )
          ) : (
            <></>
          )}
          <AttachmentPreviewRow chatGroupId={COMPOSE_SCREEN_ID} />
          <Divider />
          <View style={styles.messageBodyContainer}>
            <Text style={styles.listText}>{t('message')}</Text>
            <TextInputBox
              onChangeText={text => {
                handleTextChanged(
                  text,
                  setMessageBody,
                  characterLimitAlertShown
                )
              }}
              value={messageBody}
              onImageChange={event => handleAttachmentPaste(event.nativeEvent)}
            />
          </View>
        </View>
        <View style={styles.messageAccessoryBarContainer}>
          {enableUrgentMessage ? (
            <View style={styles.urgentRowContainer}>
              <View style={styles.urgentSwitchContainer}>
                <Switch
                  value={isUrgentMessage}
                  onValueChange={setIsUrgentMessage}
                  trackColor={{ false: styles.urgentSwitch.color }}
                />
              </View>
              <View style={styles.urgentRowTextContainer}>
                <Text>{t('urgentToggleText')}</Text>
              </View>
            </View>
          ) : null}
          <MessageAccessoryBar
            buttonNames={['photo', 'camera', 'upload']}
            chatGroupId={COMPOSE_SCREEN_ID}
          />
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  )
}

const RecipientListItem = ({
  item,
  onPress,
}: {
  item: ComposeUser | ComposeGroup
  onPress: (item: ComposeUser | ComposeGroup) => void
}) => {
  const styles = useStyles()

  return (
    <PressableOpacity onPress={() => onPress(item)}>
      <View style={styles.listItem}>
        <Text>{getRecipientName(item)}</Text>
      </View>
    </PressableOpacity>
  )
}

const getRecipientName = (recipient: ComposeUser | ComposeGroup) => {
  if (recipient.type === 'group') {
    return recipient.title
  }
  let prefix = ''
  const suffix = recipient.jobTitle ? `(${recipient.jobTitle})` : ''
  const role = getPrimaryRole(recipient.roles)

  switch (role) {
    case 'admin':
    case 'observer':
      prefix = `${t('managementTeam')}:`
      break
    case 'staff':
      prefix = `${t('staffTeam')}:`
      break
    case 'guest':
      prefix = `${t('guestTeam')}:`
      break
    case 'manager':
    case 'helper':
      if (recipient.storeId) {
        prefix = `${t('store')} ${recipient.storeId}:`
      }
      break
  }
  return `${prefix} ${recipient.firstName} ${recipient.lastName} ${suffix}`
}

const useStyles = makeStyles((theme, darkmode) => ({
  container: {
    flex: 1,
    justifyContent: 'space-between',
    backgroundColor: theme.colors?.white,
  },
  descriptionText: {
    fontSize: 16,
    paddingHorizontal: 10,
    paddingBottom: 5,
    marginTop: 5,
    color: theme.colors.grey2,
  },
  listText: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  recipientList: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    zIndex: 10,
    backgroundColor: darkmode ? '#121212' : '#f9f9f9',
  },
  recipientListContainer: {
    paddingBottom: 5,
  },
  selectedRecipientRow: {
    display: 'flex',
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: theme.colors?.grey6,
    backgroundColor: theme.colors?.white,
    alignItems: 'center',
    paddingBottom: 5,
    elevation: 2,
    paddingHorizontal: 10,
    maxHeight: '20%',
  },
  selectedList: {
    margin: 5,
  },
  selectedListContainer: {
    maxHeight: Platform.OS === 'web' ? 150 : 'auto',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  selectedListItem: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 30,
    padding: 5,
    backgroundColor: theme.colors?.grey5,
    marginRight: 5,
    borderRadius: 5,
    margin: 1,
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.18,
    shadowRadius: 1.0,
  },
  selectedListItemText: {
    fontSize: 14,
    lineHeight: 20,
    color: theme.colors?.black,
  },
  listItem: {
    backgroundColor: theme.colors?.white,
    padding: 10,
    height: 50,
    justifyContent: 'center',
    marginTop: 2,
    marginHorizontal: 2,
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.18,
    shadowRadius: 1.0,
  },
  searchInputComponent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: theme.colors?.grey6,
    paddingLeft: 5,
    borderRadius: 5,
    margin: 1,
    flexGrow: 1,
    height: 30,
  },
  searchTextInput: {
    color: theme.colors?.grey0,
    textAlignVertical: 'center',
    flexGrow: 1,
    height: 30,
    paddingVertical: 0,
  },
  messageBodyContainer: {
    paddingTop: 5,
    paddingHorizontal: 10,
    flex: 1,
  },
  messageAccessoryBarContainer: { paddingBottom: 0 },
  urgentRowContainer: {
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey11 : theme.colors.grey5,
    padding: 5,
    marginHorizontal: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: 10,
  },
  urgentRowTextContainer: { flex: 1 },
  urgentSwitch: {
    color: theme.mode === 'dark' ? theme.colors.grey6 : theme.colors.white,
  },
  urgentSwitchContainer: {
    margin: 8,
    marginRight: 12,
  },
}))
